import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { cn } from "design-system/utils"
import { ListFilter, Plus, SearchIcon } from "lucide-react"
import { useGetBackofficeScheduledReportsQuery } from "modules/backoffice/hooks/scheduledReports/useGetBackofficeScheduledReportsQuery"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Route as BackofficeScheduledReportsRoute,
  ScheduledReportsStatus,
} from "routes/_base/_auth/backoffice/scheduledReports/list/route"
import {
  BackofficeScheduledReportsSortFields,
  BackofficeScheduledReportsSortFieldsEnum,
} from "schema"
import { BackofficeScheduledReportsTable } from "./BackofficeScheduledReportsTable"

const ITEMS_PER_PAGE = 25

export const BackofficeScheduledReports = () => {
  const { t } = useTranslation()
  const [filtersVisible, setFiltersVisible] = useState(false)
  const search = BackofficeScheduledReportsRoute.useSearch()
  const navigate = BackofficeScheduledReportsRoute.useNavigate()

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: BackofficeScheduledReportsRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { query, setQuery } = useSearchQuery({
    search,
    fullPath: BackofficeScheduledReportsRoute.fullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const { onSort, sortBy, fields } = useSortBy<BackofficeScheduledReportsSortFieldsEnum[]>({
    search,
    fullPath: BackofficeScheduledReportsRoute.fullPath,
    fields: [...BackofficeScheduledReportsSortFields],
  })

  const { data, isPending } = useGetBackofficeScheduledReportsQuery({
    query: {
      query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy: sortBy ?? { field: "id", order: "asc" },
      filterBy: {
        alias: search.filterBy?.alias,
        isActive: search.status === "active",
      },
    },
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  return (
    <>
      <Tabs
        value={search.status}
        onValueChange={(status) =>
          navigate({
            search: { ...search, status: status as ScheduledReportsStatus, page: 1 },
          })
        }
      >
        <TabsList>
          {ScheduledReportsStatus.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`backoffice.scheduledReports.tabs.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="flex flex-col sm:flex-row gap-2 justify-end">
        <div
          className={cn(
            "transition-all duration-200 w-full",
            filtersVisible
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 max-h-0 max-w-0 pointer-events-none overflow-hidden",
          )}
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <SearchQuery
              placeholder={t("customers.table.search")}
              setQuery={setQuery}
              defaultValue={query}
              icon={SearchIcon}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <div
            onClick={() => setFiltersVisible((prev) => !prev)}
            className={cn(
              "h-10 w-10 flex items-center justify-center cursor-pointer shrink-0",
              filtersVisible ? "bg-primary/5 text-primary rounded-md" : "text-muted-foreground",
            )}
          >
            <ListFilter size={20} />
          </div>
          <div className="h-10">
            <Link to="/backoffice/scheduledReports/create">
              <Button variant="default" className="h-full">
                <span>
                  <Plus className="size-4" strokeWidth={2} />
                </span>
                <span>{t("backoffice.scheduledReports.createButton")}</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <BackofficeScheduledReportsTable
          scheduledReports={data}
          isPending={isPending}
          fields={fields}
          onSort={onSort}
          search={search}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
    </>
  )
}
